import React, { useState, useRef, useEffect, useCallback } from 'react';
import { avisar } from './aviso';

/* ⚠ A CAPA NÃO PODE SER SERVIDA POR URL DIRETA NUM <img> (07/09/2026).
   A rota `GET /dashboard-config/capa/:sala` exige `autenticar`, e uma
   tag de imagem NÃO manda o cabeçalho Authorization — o navegador
   pediria sem o token e levaria 401 em silêncio, mostrando cartão
   quebrado.

   O sistema já resolve isso do jeito certo na foto de perfil: busca com
   axios em `responseType: 'blob'` e cria uma URL de objeto. Aqui vale a
   mesma coisa, com um cache de módulo por dois motivos:

     1. A Home desenha até 43 cartões. Sem cache seriam 43 requisições a
        cada render, e no 4G da fábrica isso é a diferença entre abrir e
        não abrir.
     2. `createObjectURL` vaza memória se ninguém revogar. Guardando a
        URL por sala, ela é criada UMA vez por sessão.

   Exportado porque o cartão da Home usa o mesmo cache — se cada um
   tivesse o seu, a mesma capa seria baixada duas vezes. */
const cacheCapas = new Map();

/* (V1883) `base`: a Home da Administração Geral usa a MESMA peça, com as capas noutra rota (/administracao-geral/capa) */
export async function urlDaCapa(API, salaNumero, versao, base = '/dashboard-config/capa') {
  const chave = `${base}:${salaNumero}:${versao || ''}`;
  if (cacheCapas.has(chave)) return cacheCapas.get(chave);
  try {
    const r = await API.get(`${base}/${salaNumero}`, { responseType: 'blob' });
    const url = URL.createObjectURL(r.data);
    cacheCapas.set(chave, url);
    return url;
  } catch (e) {
    /* 404 é o caso normal: sala sem capa. O registro fica para a falha
       de verdade não virar mistério. */
    if (e?.response?.status !== 404) console.debug('[capa]', salaNumero, e.message);
    return null;
  }
}

/* ══════════════════════════════════════════════════════════════════════
   CAPA DA SALA — enquadrar arrastando (06/09/2026, demo aprovada)

   O QUE ELE PRODUZ: três valores. `capa` (o arquivo inteiro),
   `capa_pos` (um par de porcentagens) e `capa_zoom` (um número >= 1).
   O recorte NÃO acontece aqui e não acontece no servidor — acontece no
   CSS do cartão, com esses três valores.

   O MODELO, EM UMA FRASE: o ponto P da foto fica no ponto P da janela,
   ampliado z vezes. Não é escolha arbitrária: é a definição da
   porcentagem em `background-position` — o pixel a P% da FOTO é posto a
   P% do QUADRO. Com o `transform-origin` no mesmo par, o `scale` gira
   em volta desse mesmo pixel e ele não sai do lugar.

   ⚠ A CONTA DO ARRASTE tem UM termo só por causa disso:

       sobra = coberturaNoEixo × z − quadroNoEixo
       ΔP%   = −Δpx / sobra × 100        (travado entre 0 e 100)

   Se em vez disso o quadro usasse `object-fit: cover` com um `scale`
   por cima, dois recortes se comporiam e arrastar 10px moveria a foto
   uma distância que depende do zoom E da proporção ao mesmo tempo.

   ⚠ COM z >= 1 A FOTO SEMPRE COBRE. Não existe estado em que sobre
   faixa vazia: o erro deixa de ser possível em vez de ser avisado
   depois. É a mesma ideia das fotos guiadas da Sala 1 — mostrar o
   enquadramento na hora em vez de recusar no fim.

   ⚠ ENVELOPE ÚNICO. A regra dos dois envelopes pergunta o que a pessoa
   perde ao fechar sem querer. Aqui: nada — não se digita nada, e a foto
   ou já subiu ou não subiu. Então fecha por clique fora e por Esc.
   ══════════════════════════════════════════════════════════════════════ */

const ZOOM_MIN = 1;
/* Teto de 3: acima disso uma miniatura de 400px começa a mostrar pixel.
   Mais curso exigiria servir miniatura maior, e aí volta o peso na Home
   para quem abre no celular, no 4G. */
const ZOOM_MAX = 3;

export default function CapaSalaModal({ sala, API, onFechar, onSalvo, base = '/dashboard-config/capa', rotulo }) {
  const [pos, setPos] = useState(sala.capa_pos || '50% 50%');
  const [zoom, setZoom] = useState(Number(sala.capa_zoom) || 1);
  const [imagem, setImagem] = useState(null);      // base64 nova, se houver
  const [dimensoes, setDimensoes] = useState(null); // { w, h } da imagem exibida
  const [salvando, setSalvando] = useState(false);
  const [tocado, setTocado] = useState(false);

  const recRef = useRef(null);
  const arrasteRef = useRef(null);
  const fileRef = useRef(null);

  const [urlServidor, setUrlServidor] = useState(null);
  const urlAtual = imagem || urlServidor;

  const [px, py] = pos.split(' ').map(v => parseFloat(v) || 0);

  /* A capa que já está no servidor vem por blob autenticado — ver a
     nota de `urlDaCapa` no topo. */
  useEffect(() => {
    let vivo = true;
    if (!sala.capa) { setUrlServidor(null); return; }
    urlDaCapa(API, sala.sala_numero, sala.capa, base).then(u => { if (vivo) setUrlServidor(u); });
    return () => { vivo = false; };
  }, [API, sala.sala_numero, sala.capa]);

  /* As dimensões naturais são o que converte pixel arrastado em
     porcentagem. Sem elas não há como saber qual eixo tem sobra. */
  useEffect(() => {
    if (!urlAtual) { setDimensoes(null); return; }
    const img = new Image();
    img.onload = () => setDimensoes({ w: img.naturalWidth, h: img.naturalHeight });
    img.src = urlAtual;
  }, [urlAtual]);

  const sobra = useCallback(() => {
    const el = recRef.current;
    if (!el || !dimensoes) return { x: 0, y: 0 };
    const r = el.getBoundingClientRect();
    const rImg = dimensoes.w / dimensoes.h;
    const rQuadro = r.width / r.height;
    let cw, ch;
    if (rImg > rQuadro) { ch = r.height; cw = r.height * rImg; }
    else { cw = r.width; ch = r.width / rImg; }
    return {
      x: Math.max(0, cw * zoom - r.width),
      y: Math.max(0, ch * zoom - r.height)
    };
  }, [dimensoes, zoom]);

  const comecar = (e) => {
    if (!urlAtual) return;
    setTocado(true);
    const p = e.touches ? e.touches[0] : e;
    arrasteRef.current = { x: p.clientX, y: p.clientY, px, py };
    recRef.current?.classList.add('arrastando');
  };

  useEffect(() => {
    const mover = (e) => {
      const a = arrasteRef.current;
      if (!a) return;
      if (e.cancelable) e.preventDefault();
      const p = e.touches ? e.touches[0] : e;
      const s = sobra();
      let nx = a.px, ny = a.py;
      /* Meio pixel de folga: abaixo disso o eixo não tem curso e mexer
         nele só produziria tremor sem deslocamento. */
      if (s.x > 0.5) nx = Math.min(100, Math.max(0, a.px - (p.clientX - a.x) / s.x * 100));
      if (s.y > 0.5) ny = Math.min(100, Math.max(0, a.py - (p.clientY - a.y) / s.y * 100));
      setPos(`${Math.round(nx)}% ${Math.round(ny)}%`);
    };
    const parar = () => {
      arrasteRef.current = null;
      recRef.current?.classList.remove('arrastando');
    };
    window.addEventListener('mousemove', mover);
    window.addEventListener('touchmove', mover, { passive: false });
    window.addEventListener('mouseup', parar);
    window.addEventListener('touchend', parar);
    return () => {
      window.removeEventListener('mousemove', mover);
      window.removeEventListener('touchmove', mover);
      window.removeEventListener('mouseup', parar);
      window.removeEventListener('touchend', parar);
    };
  }, [sobra]);

  useEffect(() => {
    const tecla = (e) => { if (e.key === 'Escape') onFechar(); };
    window.addEventListener('keydown', tecla);
    return () => window.removeEventListener('keydown', tecla);
  }, [onFechar]);

  const escolher = (e) => {
    const arq = e.target.files?.[0];
    if (!arq) return;
    if (!arq.type.startsWith('image/')) return avisar('Escolha um arquivo de imagem.', 'erro');
    const leitor = new FileReader();
    leitor.onload = () => {
      setImagem(String(leitor.result));
      /* Foto nova recomeça centralizada e sem zoom: manter o
         enquadramento da foto anterior aplicaria um ponto que não
         corresponde a nada nesta. */
      setPos('50% 50%');
      setZoom(1);
      setTocado(false);
    };
    leitor.readAsDataURL(arq);
  };

  const salvar = async () => {
    setSalvando(true);
    try {
      const corpo = { capa_pos: pos, capa_zoom: Number(zoom.toFixed(2)) };
      if (imagem) corpo.capa = imagem;
      const r = await API.put(`${base}/${sala.sala_numero}`, corpo);
      onSalvo(r.data.sala);
      avisar('Capa atualizada.', 'sucesso');
      onFechar();
    } catch (err) {
      avisar(err?.response?.data?.erro || 'Não foi possível salvar a capa.', 'erro');
    } finally {
      setSalvando(false);
    }
  };

  const remover = async () => {
    setSalvando(true);
    try {
      await API.delete(`${base}/${sala.sala_numero}`);
      onSalvo({ ...sala, capa: null, capa_pos: '50% 50%', capa_zoom: 1 });
      avisar('Capa removida.', 'sucesso');
      onFechar();
    } catch (err) {
      avisar(err?.response?.data?.erro || 'Não foi possível remover a capa.', 'erro');
    } finally {
      setSalvando(false);
    }
  };

  const ajustarZoom = (v) => setZoom(Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, v)));

  return (
    <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onFechar(); }}>
      <div className="modal-rola modal" style={{ maxWidth: 380 }} role="dialog" aria-label="Capa da sala">
        <div className="modal-header">
          <div className="input-group" style={{ marginBottom: 0, gap: 2 }}>
            <label>{rotulo || `Sala ${sala.sala_numero}`}</label>
            <h3 className="modal-titulo-display">Capa da sala</h3>
          </div>
          <button className="acao-fechar" onClick={onFechar} aria-label="Fechar">✕</button>
        </div>

        <div className="modal-corpo"><div
          className={`cp-rec${tocado ? ' tocado' : ''}`}
          ref={recRef}
          onMouseDown={comecar}
          onTouchStart={comecar}
        >
          {urlAtual ? (
            <i style={{
              backgroundImage: `url("${urlAtual}")`,
              '--capa-pos': pos,
              '--capa-zoom': zoom
            }} />
          ) : (
            <span className="cp-sem">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.85"
                strokeLinecap="round" strokeLinejoin="round" width="22" height="22">
                <rect x="3" y="5" width="18" height="14" rx="2.5" />
                <circle cx="9" cy="10.5" r="1.8" />
                <path d="M3.5 17l5-5 4.5 4.5 3-3 4.5 4.5" />
              </svg>
              Sem capa — o cartão usa o ícone da sala
            </span>
          )}
          <span className="cp-grade" aria-hidden="true" />
          {urlAtual && (
            <span className="cp-dica">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.85"
                strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
                <path d="M5 12h14M8 9l-3 3 3 3M16 9l3 3-3 3" />
              </svg>
              arraste para enquadrar
            </span>
          )}
        </div>

        {urlAtual && (
          <div className="cp-zoom">
            <button className="cp-zbt" onClick={() => { setTocado(true); ajustarZoom(zoom - 0.1); }}
              aria-label="Diminuir aproximação">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.85"
                strokeLinecap="round" width="14" height="14"><path d="M5 12h14" /></svg>
            </button>
            <input className="cp-faixa" type="range" min={ZOOM_MIN * 100} max={ZOOM_MAX * 100}
              step="1" value={Math.round(zoom * 100)} aria-label="Aproximação"
              onChange={(e) => { setTocado(true); ajustarZoom(Number(e.target.value) / 100); }} />
            <button className="cp-zbt" onClick={() => { setTocado(true); ajustarZoom(zoom + 0.1); }}
              aria-label="Aumentar aproximação">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.85"
                strokeLinecap="round" width="14" height="14"><path d="M12 5v14M5 12h14" /></svg>
            </button>
          </div>
        )}

        <div className="cp-linha">
          <span className="cp-nota">O quadro é o recorte exato do cartão.</span>
          <span className="cp-valor">{pos}</span>
          <span className="cp-valor">{zoom.toFixed(2).replace('.', ',')}×</span>
        </div>

        <input ref={fileRef} type="file" accept="image/*" onChange={escolher} style={{ display: 'none' }} />

        <div className="modal-footer" style={{ flexWrap: 'wrap', justifyContent: 'flex-start' }}>
          <button className="btn btn-primary" disabled={salvando || !urlAtual} onClick={salvar}>
            {salvando ? 'Salvando…' : 'Salvar enquadramento'}
          </button>
          <button className="btn" disabled={salvando} onClick={() => fileRef.current?.click()}>
            {urlAtual ? 'Trocar foto' : 'Escolher imagem'}
          </button>
          {urlAtual && (
            <button className="btn" disabled={salvando} onClick={() => { setPos('50% 50%'); setZoom(1); }}>
              Centralizar
            </button>
          )}
          {sala.capa && (
            <button className="btn" disabled={salvando} onClick={remover}>Remover capa</button>
          )}
        </div></div>
      </div>
    </div>
  );
}
